import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import {
  IdCard, Upload, Camera, Lock, FileText, Loader2, Eye, RefreshCw, History,
  ShieldAlert, CheckCircle2, X, Link2, ChevronDown, ChevronRight, ZoomIn, ZoomOut, Crop,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import DocumentCropDialog from '@/components/personnel/DocumentCropDialog';

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
const TONE = {
  Verified: 'text-emerald-700 bg-emerald-50', 'On File': 'text-emerald-700 bg-emerald-50',
  Uploaded: 'text-blue-700 bg-blue-50', 'Pending Review': 'text-amber-700 bg-amber-50',
  'Pending Verification': 'text-amber-700 bg-amber-50',
  Rejected: 'text-rose-600 bg-rose-50', Expired: 'text-rose-600 bg-rose-50', Missing: 'text-rose-600 bg-rose-50',
};
const Pill = ({ s }) => <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${TONE[s] || 'text-slate-500 bg-slate-100'}`}>{s || '—'}</span>;

const imgUrl = (pid, doc, token, full) => {
  // Cache-buster tied to the document's last update so the thumbnail and viewer
  // refresh immediately after a crop/replace (same doc id would otherwise keep
  // showing the browser's already-decoded old image).
  const v = encodeURIComponent(doc.uploaded_at || doc.versions_count || '');
  return `${API}/personnel/${pid}/identity-documents/${doc.id}/image${full ? '?view=full&' : '?'}auth=${token}${v ? `&v=${v}` : ''}`;
};

// A hidden file input rendered as a small labelled button.
const PickBtn = ({ label, icon: Icon, accept, capture, onPick, testid, busy }) => (
  <label className="inline-flex cursor-pointer items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-[10px] font-semibold text-[#1763B6] hover:bg-blue-50" data-testid={testid}>
    {busy ? <Loader2 size={11} className="animate-spin" /> : <Icon size={11} />}{label}
    <input type="file" accept={accept} capture={capture} className="hidden" onChange={onPick} />
  </label>
);

// Thumbnail area for a tile (authorized -> real image / PDF tile / Missing; else Restricted).
const TileThumb = ({ pid, doc, token, authorized, onOpen, testid }) => {
  const [err, setErr] = useState(false);
  if (!authorized) return (
    <div className="flex h-28 w-full flex-col items-center justify-center gap-1 rounded-lg border border-slate-200 bg-slate-50 text-slate-400" data-testid={`${testid}-restricted`}>
      <Lock size={18} /><span className="text-[10px] font-semibold">Restricted</span>
    </div>
  );
  if (!doc) return (
    <div className="flex h-28 w-full flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-slate-400" data-testid={`${testid}-missing`}>
      <IdCard size={18} /><span className="text-[10px] font-semibold">Not uploaded</span>
    </div>
  );
  if (doc.is_pdf) return (
    <button onClick={onOpen} className="flex h-28 w-full flex-col items-center justify-center gap-1 rounded-lg border border-slate-200 bg-slate-100 text-slate-500 hover:bg-slate-200" data-testid={testid}>
      <FileText size={22} /><span className="text-[10px] font-semibold">PDF — view</span>
    </button>
  );
  return (
    <button onClick={onOpen} className="group relative h-28 w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-100" data-testid={testid}>
      {err ? <span className="flex h-full w-full items-center justify-center text-[10px] text-slate-400">Image unavailable</span>
        : <img src={imgUrl(pid, doc, token)} alt="" onError={() => setErr(true)} className="h-full w-full object-cover transition-transform group-hover:scale-105" />}
      <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-black/45 py-0.5 text-[10px] font-semibold text-white opacity-0 transition-opacity group-hover:opacity-100"><Eye size={11} />View</span>
    </button>
  );
};

// Generic tile shell: thumbnail + title + status + expiration/secondary line + actions.
const Tile = ({ testid, title, highlySensitive, children }) => (
  <div className="flex w-48 shrink-0 snap-start flex-col rounded-xl border border-slate-200 bg-white p-2 shadow-sm md:w-52" data-testid={testid}>
    <div className="mb-1 flex items-start justify-between gap-1">
      <span className="text-[11px] font-semibold leading-tight text-[#0E2A47]">{title}</span>
      {highlySensitive && <ShieldAlert size={13} className="mt-0.5 shrink-0 text-rose-500" title="Highly sensitive" />}
    </div>
    {children}
  </div>
);

const IdentityDocuments = ({ personnelId, token, canManage, dlExpiration, dlExpired, dlState }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState('');        // identity_class currently uploading
  const [viewer, setViewer] = useState(null);  // { title, highly_sensitive, sides:[{key,label,doc,uploadClass}] }
  const [side, setSide] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [showLink, setShowLink] = useState(false);
  const [linkSel, setLinkSel] = useState({});   // docId -> class
  const [confirmReplace, setConfirmReplace] = useState(null); // { cls, file }
  const [cropState, setCropState] = useState(null); // { src, cls, isReplace }

  const load = async () => {
    try {
      const r = await axios.get(`${API}/personnel/${personnelId}/identity-documents`, auth(token));
      setData(r.data); setErr(false);
    } catch (e) { setErr(true); setData({ authorized: false, can_manage: false, classes: [], documents: [], unclassified: [] }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [personnelId]);

  const doUpload = async (cls, file) => {
    if (!file) return;
    setBusy(cls);
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(`${API}/personnel/${personnelId}/identity-documents?identity_class=${cls}`, fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Document uploaded (pending verification)');
      await load();
    } catch (err2) { toast.error(err2.response?.data?.detail || 'Upload failed'); }
    setBusy('');
  };
  const isImageFile = (f) => !!(f && f.type && f.type.startsWith('image/'));
  // Read the chosen image into the crop dialog; PDFs/non-images bypass cropping.
  const startCrop = (cls, file, isReplace) => {
    const reader = new FileReader();
    reader.onload = () => setCropState({ src: reader.result, cls, isReplace });
    reader.readAsDataURL(file);
  };
  const onCropConfirm = (blob) => {
    const c = cropState; if (!c) return;
    const file = new File([blob], 'document.jpg', { type: 'image/jpeg' });
    setCropState(null);
    if (c.isReplace) setConfirmReplace({ cls: c.cls, file });
    else doUpload(c.cls, file);
  };
  // New upload (no existing version to preserve) — crop images, upload PDFs directly.
  const upload = (cls, e) => {
    const file = e.target.files?.[0]; if (e.target) e.target.value = '';
    if (!file) return;
    if (isImageFile(file)) startCrop(cls, file, false);
    else doUpload(cls, file);
  };
  // Replace an existing version — crop images first, then require explicit confirmation.
  const pickReplace = (cls, e) => {
    const file = e.target.files?.[0]; if (e.target) e.target.value = '';
    if (!file) return;
    if (isImageFile(file)) startCrop(cls, file, true);
    else setConfirmReplace({ cls, file });
  };

  // Open the cropper on an already-uploaded image. Fetch it as a same-origin
  // data URL first so the canvas is not tainted (needed for auto-detect + save).
  const cropExisting = async (cls, doc) => {
    try {
      const r = await axios.get(imgUrl(personnelId, doc, token, true), { ...auth(token), responseType: 'blob' });
      const reader = new FileReader();
      reader.onload = () => setCropState({ src: reader.result, cls, isReplace: true });
      reader.readAsDataURL(r.data);
    } catch (e) { toast.error('Could not open image for cropping'); }
  };

  const setStatus = async (docId, status) => {
    try {
      await axios.patch(`${API}/personnel/${personnelId}/identity-documents/${docId}`, { status }, auth(token));
      toast.success(`Marked ${status}`); await load(); setViewer(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
  };

  const link = async (docId) => {
    const cls = linkSel[docId];
    if (!cls) { toast.error('Choose a document type first'); return; }
    try {
      await axios.patch(`${API}/personnel/${personnelId}/identity-documents/${docId}`, { identity_class: cls }, auth(token));
      toast.success('Linked to identity tile');
      setLinkSel((p) => { const n = { ...p }; delete n[docId]; return n; });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not link'); }
  };

  const openViewer = (tile) => { setViewer(tile); setSide(0); setZoom(1); };

  if (!data) return <div className="py-4 text-center text-slate-300"><Loader2 className="mx-auto animate-spin" size={18} /></div>;
  if (err) return <div className="rounded-lg bg-rose-50 px-3 py-2 text-[11px] text-rose-600" data-testid="pc-identity-error">Could not load identification documents. Please try again.</div>;

  const authorized = data.authorized;
  const docs = data.documents || [];
  const classes = data.classes || [];
  const byClass = Object.fromEntries(docs.map((d) => [d.identity_class, d]));
  const present = new Set(docs.map((d) => d.identity_class));
  const labelFor = (k) => (classes.find((c) => c.key === k) || {}).label || k;

  // ---- Two-sided tile (Driver's License, State ID) ----
  const twoSided = (group, frontClass, backClass, title) => {
    const front = byClass[frontClass], back = byClass[backClass];
    const primary = front || back;
    const extraLine = group === 'drivers_license' ? (
      <div className="mt-0.5 text-[9px] text-slate-400">
        {dlState ? `${dlState} · ` : ''}Exp {dlExpiration || '—'}{dlExpired ? ' (expired)' : ''}
      </div>
    ) : null;
    return (
      <Tile key={group} testid={`pc-id-tile-${group}`} title={title}>
        <TileThumb pid={personnelId} doc={primary} token={token} authorized={authorized} testid={`pc-id-thumb-${group}`} onOpen={() => openViewer({ kind: 'two', title, group, frontClass, backClass })} />
        <div className="mt-1 flex items-center justify-between">
          <Pill s={primary ? primary.status : 'Missing'} />
          <span className="text-[9px] text-slate-400">{front && back ? 'Front + Back' : front ? 'Front only' : back ? 'Back only' : ''}</span>
        </div>
        {extraLine}
        <div className="mt-1.5 flex flex-wrap gap-1">
          {authorized && primary && <button onClick={() => openViewer({ kind: 'two', title, group, frontClass, backClass })} className="inline-flex items-center gap-1 rounded border border-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-[#1763B6] hover:bg-blue-50" data-testid={`pc-id-view-${group}`}><Eye size={11} />View</button>}
          {canManage && !primary && (
            <>
              <PickBtn label="File" icon={Upload} accept="image/*,application/pdf" onPick={(e) => upload(frontClass, e)} testid={`pc-id-upload-${group}`} busy={busy === frontClass} />
              <PickBtn label="Photo" icon={Camera} accept="image/*" capture="environment" onPick={(e) => upload(frontClass, e)} testid={`pc-id-photo-${group}`} />
            </>
          )}
          {canManage && primary && (<>
            <PickBtn label="Replace" icon={RefreshCw} accept="image/*,application/pdf" onPick={(e) => pickReplace(front ? frontClass : backClass, e)} testid={`pc-id-replace-${group}`} busy={busy === frontClass || busy === backClass} />
            {!primary.is_pdf && <button onClick={() => cropExisting(front ? frontClass : backClass, primary)} className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-[10px] font-semibold text-[#1763B6] hover:bg-blue-50" data-testid={`pc-id-crop-${group}`}><Crop size={11} />Crop</button>}
          </>)}
        </div>
        {canManage && primary && !back && (
          <div className="mt-1 rounded bg-amber-50 px-1.5 py-1 text-[9px] text-amber-700">Back side missing</div>
        )}
      </Tile>
    );
  };

  // ---- Single-document tile (SSN card always shown; passport/PRC/EAD/other when present) ----
  const singleTile = (cls, { always } = {}) => {
    const doc = byClass[cls];
    if (!doc && !always) return null;
    const meta = classes.find((c) => c.key === cls) || {};
    const title = meta.label || labelFor(cls);
    return (
      <Tile key={cls} testid={`pc-id-tile-${cls}`} title={title} highlySensitive={!!meta.highly_sensitive}>
        <TileThumb pid={personnelId} doc={doc} token={token} authorized={authorized} testid={`pc-id-thumb-${cls}`} onOpen={() => openViewer({ kind: 'single', title, highly_sensitive: !!meta.highly_sensitive, cls })} />
        <div className="mt-1 flex items-center justify-between">
          <Pill s={doc ? doc.status : 'Missing'} />
          {doc && doc.versions_count > 0 && <span className="text-[9px] text-slate-400">v{doc.versions_count + 1}</span>}
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1">
          {authorized && doc && <button onClick={() => openViewer({ kind: 'single', title, highly_sensitive: !!meta.highly_sensitive, cls })} className="inline-flex items-center gap-1 rounded border border-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-[#1763B6] hover:bg-blue-50" data-testid={`pc-id-view-${cls}`}><Eye size={11} />View</button>}
          {canManage && !doc && (
            <>
              <PickBtn label="File" icon={Upload} accept="image/*,application/pdf" onPick={(e) => upload(cls, e)} testid={`pc-id-upload-${cls}`} busy={busy === cls} />
              <PickBtn label="Photo" icon={Camera} accept="image/*" capture="environment" onPick={(e) => upload(cls, e)} testid={`pc-id-photo-${cls}`} />
            </>
          )}
          {canManage && doc && <PickBtn label="Replace" icon={RefreshCw} accept="image/*,application/pdf" onPick={(e) => pickReplace(cls, e)} testid={`pc-id-replace-${cls}`} busy={busy === cls} />}
          {canManage && doc && !doc.is_pdf && <button onClick={() => cropExisting(cls, doc)} className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-[10px] font-semibold text-[#1763B6] hover:bg-blue-50" data-testid={`pc-id-crop-${cls}`}><Crop size={11} />Crop</button>}
        </div>
      </Tile>
    );
  };

  // Remaining single classes to offer in an "add" menu (exclude two-sided + ssn + already present).
  const SINGLE_ADDABLE = ['passport', 'permanent_resident_card', 'work_authorization_card', 'other_identity'];
  const otherPresent = SINGLE_ADDABLE.filter((k) => present.has(k));
  const addable = classes.filter((c) => !present.has(c.key) && c.key !== 'social_security_card');
  const unclassified = data.unclassified || [];

  return (
    <div data-testid="pc-identity-documents">
      {!authorized && (
        <div className="mb-2 flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500" data-testid="pc-id-restricted-banner">
          <Lock size={13} className="mt-0.5 shrink-0" /><span>Identity-document images are restricted to authorized Administration / HR personnel. You can see which documents are on file, but not their images.</span>
        </div>
      )}

      {/* Mobile: horizontal scroll within the carousel only. Desktop: wrap side by side. */}
      <div className="flex gap-3 overflow-x-auto pb-2 snap-x md:flex-wrap md:overflow-visible" data-testid="pc-id-carousel">
        {(byClass['drivers_license_front'] || byClass['drivers_license_back'] || canManage)
          && twoSided('drivers_license', 'drivers_license_front', 'drivers_license_back', "Driver's License")}
        {/* SSN card — always present for an authorized HR/Admin viewer or manager;
            also shown (Restricted) to other viewers when an SSN is on file. */}
        {(authorized || canManage || byClass['social_security_card']) && singleTile('social_security_card', { always: authorized || canManage })}
        {/* State ID only when a side exists */}
        {(byClass['state_id_front'] || byClass['state_id_back'])
          && twoSided('state_id', 'state_id_front', 'state_id_back', 'State ID')}
        {otherPresent.map((k) => singleTile(k))}
        {/* Add another identity document */}
        {canManage && addable.length > 0 && <AddTile classes={addable} busy={busy} onUpload={upload} />}
        {docs.length === 0 && !canManage && !authorized && (
          <div className="py-6 text-center text-xs text-slate-400" data-testid="pc-id-empty">No identification documents on file.</div>
        )}
      </div>

      {/* Link existing historical uploads (only when genuinely unclassified docs exist) */}
      {canManage && unclassified.length > 0 && (
        <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50/70" data-testid="pc-id-link-existing">
          <button onClick={() => setShowLink((s) => !s)} className="flex w-full items-center justify-between px-3 py-2 text-[11px] font-semibold text-[#0E2A47]" data-testid="pc-id-link-toggle">
            <span className="flex items-center gap-1.5"><Link2 size={13} className="text-[#1763B6]" />Link Existing Document ({unclassified.length})</span>
            {showLink ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          {showLink && (
            <div className="space-y-2 px-3 pb-2">
              <p className="text-[10px] text-slate-500">These historical uploads aren't linked to an identity tile yet. Linking does not create a duplicate.</p>
              {unclassified.map((u) => (
                <div key={u.id} className="flex flex-col gap-1.5 rounded border border-slate-200 bg-white p-2 sm:flex-row sm:items-center" data-testid={`pc-id-unc-${u.id}`}>
                  <span className="min-w-0 flex-1 break-words text-[11px] text-slate-600" title={u.file_name || u.label}><span className="font-semibold text-[#0E2A47]">{u.label}</span> · {u.file_name || (u.is_pdf ? 'PDF' : 'image')}</span>
                  <div className="flex items-center gap-1.5">
                    <select value={linkSel[u.id] || ''} onChange={(e) => setLinkSel((p) => ({ ...p, [u.id]: e.target.value }))} className="rounded border border-slate-300 px-1.5 py-1 text-[10px]" data-testid={`pc-id-unc-select-${u.id}`}>
                      <option value="">Link as…</option>
                      {classes.filter((c) => !present.has(c.key)).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                    <button onClick={() => link(u.id)} className="inline-flex items-center gap-1 rounded bg-[#1763B6] px-2 py-1 text-[10px] font-semibold text-white hover:bg-[#12579f]" data-testid={`pc-id-unc-save-${u.id}`}><Link2 size={11} />Link</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {data.notice && <p className="mt-2 text-[10px] leading-snug text-slate-400" data-testid="pc-id-notice">{data.notice}</p>}

      {/* Full-size viewer with Front/Back navigation + zoom */}
      <Dialog open={!!viewer} onOpenChange={(o) => { if (!o) setViewer(null); }}>
        <DialogContent className="max-w-3xl" data-testid="pc-id-viewer">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><IdCard size={18} className="text-[#1763B6]" />{viewer?.title}
              {viewer?.highly_sensitive && <span className="inline-flex items-center gap-1 rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-600"><ShieldAlert size={11} />Highly sensitive</span>}
            </DialogTitle>
            <DialogDescription className="text-[11px]">Confidential identity document. Access is audit-logged. Do not export or share.</DialogDescription>
          </DialogHeader>
          {viewer && (() => {
            const v = viewer;
            const sides = v.kind === 'two'
              ? [{ key: 'front', label: 'Front', doc: byClass[v.frontClass], uploadClass: v.frontClass },
                 { key: 'back', label: 'Back', doc: byClass[v.backClass], uploadClass: v.backClass }]
              : [{ key: 'single', label: v.title, doc: byClass[v.cls], uploadClass: v.cls }];
            const cur = sides[Math.min(side, sides.length - 1)];
            const doc = cur.doc;
            return (
              <div className="space-y-3">
                {sides.length > 1 && (
                  <div className="flex gap-1" data-testid="pc-id-viewer-sides">
                    {sides.map((s, i) => (
                      <button key={s.key} onClick={() => { setSide(i); setZoom(1); }} className={`rounded-full px-3 py-1 text-xs font-semibold ${i === side ? 'bg-[#0E2A47] text-white' : 'bg-slate-100 text-slate-600'}`} data-testid={`pc-id-viewer-side-${s.key}`}>{s.label}{s.doc ? '' : ' (missing)'}</button>
                    ))}
                  </div>
                )}
                {doc ? (
                  <>
                    {!doc.is_pdf && (
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setZoom((z) => Math.max(1, +(z - 0.5).toFixed(1)))} className="rounded border border-slate-200 p-1 text-slate-600 hover:bg-slate-50" data-testid="pc-id-zoom-out"><ZoomOut size={14} /></button>
                        <span className="w-10 text-center text-[11px] text-slate-500">{Math.round(zoom * 100)}%</span>
                        <button onClick={() => setZoom((z) => Math.min(4, +(z + 0.5).toFixed(1)))} className="rounded border border-slate-200 p-1 text-slate-600 hover:bg-slate-50" data-testid="pc-id-zoom-in"><ZoomIn size={14} /></button>
                      </div>
                    )}
                    <div className="flex max-h-[72vh] items-center justify-center overflow-auto rounded-lg bg-slate-100 p-2">
                      {doc.is_pdf ? (
                        <object data={imgUrl(personnelId, doc, token, true)} type="application/pdf" className="h-[72vh] w-full" data-testid="pc-id-viewer-pdf">
                          <a href={imgUrl(personnelId, doc, token, true)} target="_blank" rel="noreferrer" className="text-sm text-[#1763B6] underline">Open PDF</a>
                        </object>
                      ) : (
                        <img src={imgUrl(personnelId, doc, token, true)} alt={cur.label} style={{ width: zoom > 1 ? `${zoom * 100}%` : undefined, maxWidth: 'none' }} className={`${zoom > 1 ? 'w-full' : 'w-full max-h-[70vh]'} object-contain`} data-testid="pc-id-viewer-img" />
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-600">
                      <div className="flex items-center justify-between"><span className="text-slate-400">Status</span><Pill s={doc.status} /></div>
                      <div className="flex items-center justify-between"><span className="text-slate-400">File</span><span className="truncate" title={doc.file_name}>{doc.file_name || '—'}</span></div>
                      <div className="flex items-center justify-between"><span className="text-slate-400">Uploaded</span><span>{(doc.uploaded_at || '').slice(0, 10) || '—'}{doc.uploaded_by ? ` · ${doc.uploaded_by}` : ''}</span></div>
                      <div className="flex items-center justify-between"><span className="text-slate-400">Verified by</span><span>{doc.verified_by || '—'}</span></div>
                    </div>
                    {doc.versions_count > 0 && (
                      <div className="rounded-lg bg-slate-50 p-2" data-testid="pc-id-viewer-history">
                        <div className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-slate-500"><History size={11} />Previous versions ({doc.versions_count})</div>
                        {doc.version_history.map((v, i) => (
                          <div key={i} className="flex items-center justify-between text-[10px] text-slate-500"><span>{v.file_name || 'version'}</span><span>{(v.uploaded_at || '').slice(0, 10)}{v.uploaded_by ? ` · ${v.uploaded_by}` : ''}</span></div>
                        ))}
                      </div>
                    )}
                    {canManage && (() => {
                      const st = doc.status;
                      const verified = st === 'Verified';
                      const rejected = st === 'Rejected';
                      const pending = !verified && !rejected; // Uploaded / Pending Review / Pending Verification
                      return (
                        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2" data-testid="pc-id-viewer-actions">
                          {/* Replace is always available, positioned far left */}
                          <PickBtn label="Replace" icon={RefreshCw} accept="image/*,application/pdf" onPick={(e) => pickReplace(cur.uploadClass, e)} testid="pc-id-viewer-replace" busy={busy === cur.uploadClass} />
                          {!doc.is_pdf && (
                            <button onClick={() => cropExisting(cur.uploadClass, doc)} className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-[10px] font-semibold text-[#1763B6] hover:bg-blue-50" data-testid="pc-id-viewer-crop"><Crop size={11} />Crop / Adjust</button>
                          )}
                          <div className="ml-auto flex items-center gap-2">
                            {/* Verified indicator kept separate from the action buttons */}
                            {verified && (
                              <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700" data-testid="pc-id-verified-indicator">
                                <CheckCircle2 size={13} />Verified{doc.verified_by ? ` · ${doc.verified_by}` : ''}{doc.verified_at ? ` · ${(doc.verified_at || '').slice(0, 10)}` : ''}
                              </span>
                            )}
                            {rejected && (
                              <span className="inline-flex items-center gap-1 rounded bg-rose-50 px-2 py-1 text-[11px] font-semibold text-rose-600" data-testid="pc-id-rejected-indicator">
                                <X size={13} />Rejected — upload a replacement to re-review
                              </span>
                            )}
                            {pending && (<>
                              <button onClick={() => setStatus(doc.id, 'Verified')} className="inline-flex items-center gap-1 rounded bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700" data-testid="pc-id-verify"><CheckCircle2 size={13} />Mark Verified</button>
                              <button onClick={() => setStatus(doc.id, 'Rejected')} className="inline-flex items-center gap-1 rounded border border-rose-300 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50" data-testid="pc-id-reject"><X size={13} />Reject</button>
                            </>)}
                          </div>
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 py-10 text-slate-400" data-testid="pc-id-viewer-missing">
                    <IdCard size={28} /><span className="text-xs font-semibold">{cur.label} not uploaded</span>
                    {canManage && (
                      <div className="flex gap-2">
                        <PickBtn label={`Upload ${cur.label}`} icon={Upload} accept="image/*,application/pdf" onPick={(e) => upload(cur.uploadClass, e)} testid="pc-id-viewer-upload" busy={busy === cur.uploadClass} />
                        <PickBtn label="Take Photo" icon={Camera} accept="image/*" capture="environment" onPick={(e) => upload(cur.uploadClass, e)} testid="pc-id-viewer-photo" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Replace confirmation — preserves the current version in history and sets the new one to Pending */}
      <Dialog open={!!confirmReplace} onOpenChange={(o) => { if (!o) setConfirmReplace(null); }}>
        <DialogContent className="max-w-md" data-testid="pc-id-replace-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><RefreshCw size={16} className="text-[#1763B6]" />Confirm document replacement</DialogTitle>
            <DialogDescription className="text-[12px]">
              The current version will be preserved in document history. The new file will be saved as <span className="font-semibold">Pending Verification</span> and must be verified again.
            </DialogDescription>
          </DialogHeader>
          {confirmReplace && <p className="truncate text-[12px] text-slate-500" title={confirmReplace.file?.name}>New file: <span className="font-medium text-slate-700">{confirmReplace.file?.name}</span></p>}
          <div className="mt-2 flex justify-end gap-2">
            <button onClick={() => setConfirmReplace(null)} className="rounded border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50" data-testid="pc-id-replace-cancel">Cancel</button>
            <button onClick={() => { const c = confirmReplace; setConfirmReplace(null); if (c) doUpload(c.cls, c.file); }} className="inline-flex items-center gap-1 rounded bg-[#1763B6] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#12579f]" data-testid="pc-id-replace-confirm-btn"><RefreshCw size={13} />Confirm Replacement</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Crop / adjust dialog for identity-document images (new upload, replace, or re-crop) */}
      {cropState && (
        <DocumentCropDialog src={cropState.src} onConfirm={onCropConfirm} onCancel={() => setCropState(null)} />
      )}
    </div>
  );
};

// Small tile to add a new single identity document type.
const AddTile = ({ classes, busy, onUpload }) => {
  const [cls, setCls] = useState('');
  return (
    <div className="flex w-48 shrink-0 snap-start flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-3 text-center md:w-52" data-testid="pc-id-add-other">
      <div className="mb-1 text-[11px] font-semibold text-[#0E2A47]">Add identity document</div>
      <select value={cls} onChange={(e) => setCls(e.target.value)} className="mb-2 w-full rounded border border-slate-300 px-1.5 py-1 text-[10px]" data-testid="pc-id-add-select">
        <option value="">Select type…</option>
        {classes.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
      </select>
      {cls && (
        <div className="flex gap-1">
          <PickBtn label="File" icon={Upload} accept="image/*,application/pdf" onPick={(e) => { onUpload(cls, e); setCls(''); }} testid="pc-id-add-upload" busy={busy === cls} />
          <PickBtn label="Photo" icon={Camera} accept="image/*" capture="environment" onPick={(e) => { onUpload(cls, e); setCls(''); }} testid="pc-id-add-photo" />
        </div>
      )}
    </div>
  );
};

export default IdentityDocuments;
